<template>
  <div class="product-price-wrapper large">
    <DetailsModal
      v-if="showModal"
      :name="modalName"
      :badges-list="price.badgesList"
      @close="closeModal"
    />
    <div
      v-if="showPriceType"
      class="price-type-container margin-bottom-xxs"
      :class="{
        'flex-container': showPriceType && !price.flags.isLastAct,
        'inline-block-container promo-badge': price.flags.isLastAct,
      }"
    >
      <span
        v-if="!price.flags.isLastAct"
        class="promo-badge"
        :class="badgeColor"
      >{{ getPriceType }}</span>
      <div
        v-if="price.flags.isLastAct"
        class="last-act-wrapper"
      >
        <span> {{ price.lastActFormatted }} - Final Sale </span>
        <button
          type="button"
          aria-label="Toggletip"
          class="help-button promo-badge"
          @click="toggletip.toggle"
        />
        <Toggletip
          id="last-act-toggletip-mcom"
          ref="toggletip"
          position="top"
          :width="327"
        >
          <div id="final-sale-tooltip-wrapper">
            <div id="final-sale-tooltip">
              <p>
                Last Act items are final sale and cannot be returned. Extra savings do not apply.
                <br>
                <a
                  href="https://customerservice-macys.com/articles/what-is-macys-return-policy#Last-Act-Merchandise"
                  target="_blank"
                  class="details-link"
                >
                  Details
                </a>
              </p>
            </div>
            <div class="final-sale-tooltip-icon tooltip-large" />
          </div>
        </Toggletip>
      </div>
    </div>
    <div
      :class="{ 'promo': isPromo }"
      class="price-wrapper"
    >
      <span
        tabindex="0"
        :aria-label="screenReaderTextForPrice"
      >
        <span
          class="base-price"
          :class="{
            'alt-display': isTwoRowsDisplay,
          }"
        >
          <span
            class="price-lg temp-price-heavy"
            :class="{
              'price-red': price.flags.isLowestPriceRed,
            }"
          >
            <span>
              <span
                v-if="isNow"
                class="price-red"
              > Now </span>
              <span
                role="text"
                tabindex="0"
                :aria-label="'Current Price ' + (price.lowestSalePriceTier?.values && price.lowestSalePriceTier?.values[0]?.formattedValue)"
              > {{ price.lowestSalePriceTier?.values && price.lowestSalePriceTier?.values[0]?.formattedValue }} </span>
              <span
                v-if="price.percentOff"
                :class="{'font-weight-sm body-regular price-red': isPercentageStyleUpdate }"
              > ({{ price.percentOff }}% off)</span>
            </span>
          </span>
          <span
            v-if="price.flags.isWithOfferPromo"
            class="withOffer body-regular"
          > with {{ wordCode }} {{ price.lowestSalePriceTier.promoCode }}  </span>
          <span
            v-if="isFirstRowPriceStrike"
            class="price-strike margin-left-xxs"
            :class="{'body-regular': isPercentageStyleUpdate }"
          >
            {{ price.regularPriceTier?.values && price.regularPriceTier?.values[0]?.formattedValue }}
          </span>
        </span>
        <span
          class="extra-price"
        >
          <span
            v-if="!isMiddlePriceRemoved && price.flags.isWithOfferPromo && price.flags.isTwoTier"
            class="body-regular"
          > Sale {{ salePriceWithOffer }}</span>
          <span
            v-if="isSecondRowPriceStrike"
            class="price-strike"
            :class="{'body-regular': isPercentageStyleUpdate }"
            role="text"
            tabindex="0"
            :aria-label="'Previous Price ' + (price.regularPriceTier?.values && price.regularPriceTier?.values[0]?.formattedValue)"
          >
            {{ price.regularPriceTier?.values && price.regularPriceTier?.values[0]?.formattedValue }}
          </span>

          <span
            v-if="!price.flags.isLastAct && price.promoCodeHeader"
            class="promocode body-regular"
          >
            {{ price.promoCodeHeader }}
          </span>
          <span
            v-if="price.priceType?.giftPriceLabelToAllUpcs"
            class="body-regular value-price"
          >
            {{ price.giftPriceLabel }}
          </span>

        </span>
      </span>

      <button
        class="link-body"
        :class="{ 'alt-display-button': isTwoRowsDisplay }"
        @click="toggleDetailsModal"
      >
        Details
      </button>
      <div
        v-if="additionalFeeInfo.bigTicketFees || additionalFeeInfo.smallTicketFees"
        class="additional-fees medium"
      >
        <div v-if="additionalFeeInfo.bigTicketFees">
          <span>{{ additionalFeeInfo.formattedPrice }} with </span>
          <span v-if="!additionalFeeInfo.isParcelShipping">scheduled</span> delivery
        </div>
        <div v-else>
          {{ additionalFeeInfo.formattedPrice }} with delivery
        </div>
      </div>
      <div
        v-if="((price.flags.creditFinance && isCcfp) || (price.flags.klarna && priceWithinRange)) && !isProductPerksEnabled"
        :class="isKlarnaSpacingEnabled ? '' : 'margin-top-xs'"
      >
        <Divider v-if="!isKlarnaSpacingEnabled" />
        <Ccfp
          :price="price"
        />
        <Klarna
          v-if="price.flags.klarna"
          :price="price"
        />
        <Divider v-if="!isKlarnaSpacingEnabled" />
      </div>

      <div
        v-if="price.offers.regularOffer && !isProductPerksEnabled"
        class="secondary-promo"
      >
        <button
          class="link-sm margin-top-xs secondary-promo"
          @click="toggleDetailsModal"
        >
          <span>{{ price.offers.regularOffer.header }}</span>
          <span v-if="price.offers.isMultipleOffers">  + More</span>
        </button>
      </div>
      <div
        v-if="price.fauxOffer && isProductPerksEnabled"
        class="secondary-promo"
      >
        <button
          class="link-sm margin-top-xs secondary-promo"
          @click="toggleDetailsModal"
        >
          <span>{{ price.fauxOffer.header }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed, ref, defineAsyncComponent, provide, toRef,
} from 'vue';
import Toggletip from '@atomic-ui/toggletip';
import useModal from '../../../composables/useModal';
import { PRICE_OVERLAY_DATA, PRICE_TYPE_RELATION, CLEARANCE_IDS } from './constants';
import { PRICE_OVERLAY_DATA as PRICE_OVERLAY_DATA_BCOM } from './bcom/constants';
import Ccfp from '../../credit-finance/components/Ccfp';
import formatCurrency from '../../../utils/currencyFormatter';
import { fireDetailsAnalytics } from './utils.common';
import Divider from '../../../components/common/Divider';
// Variant component for Pricing Simplification experiment
import Klarna from '../../credit-finance/components/Klarna';
import useKlarna from '../../credit-finance/composables/useKlarna';
import { useCcfp } from '../../credit-finance/composables/useCcfp';
import { useBrand } from '../../../composables/useBrand';

const DetailsModal = defineAsyncComponent(() => import(/* webpackChunkName: 'DetailsModal' */ './DetailsModal'));

const props = defineProps({
  name: {
    type: String,
    default: 'price-section',
  },
  price: {
    type: Object,
    default: null,
  },
  additionalFee: {
    type: Object,
    default: null,
  },
  isKlarnaSpacingEnabled: {
    type: Boolean,
    default: false,
  },
  isWordCodeEnabled: {
    type: Boolean,
    default: false,
  },
  isPercentageStyleUpdate: {
    type: Boolean,
    default: false,
  },
  isMiddlePriceRemoved: {
    type: Boolean,
    default: false,
  },
  isProductPerksEnabled: {
    type: Boolean,
    default: false,
  },
});
const { priceWithinRange } = useKlarna(toRef(props, 'price'));
const { isCcfp } = useCcfp(toRef(props, 'price'));

const { isBcom } = useBrand();
const priceOverlayData = computed(() => props.price);
provide(isBcom ? PRICE_OVERLAY_DATA_BCOM : PRICE_OVERLAY_DATA, priceOverlayData);

const emit = defineEmits(['open-details-modal', 'close-details-modal']);

const { open } = useModal();

const toggletip = ref(null);

const isPromo = computed(() => props.price?.promoCodeHeader || props.price?.flags.isWithOfferPromo);
const showPriceType = computed(() => (props.price.flags?.isLastAct || props.price.priceType?.text) && !props.price.flags?.isEverydayValue);
const additionalFeeInfo = computed(() => {
  const deliveryMethod = props.additionalFee?.deliveryMethod;
  const isParcelShipping = deliveryMethod?.type === '5' || deliveryMethod?.type === '6';
  const bigTicketFees = props.additionalFee?.deliveryMethod?.totalDeliveryFee;
  const smallTicketFees = props.additionalFee?.surchargeAmount;

  const lowestSalePriceTier = props.price.lowestSalePriceTier?.values?.[0]?.value;
  const additionalFee = bigTicketFees || smallTicketFees;
  const formattedPrice = formatCurrency(lowestSalePriceTier + additionalFee);

  return {
    bigTicketFees: props.additionalFee?.deliveryMethod?.totalDeliveryFee,
    smallTicketFees: props.additionalFee?.surchargeAmount,
    isParcelShipping,
    formattedPrice,
  };
});

const modalName = `${props.name}-detail-modal`;
const showModal = ref(false);

const screenReaderTextForPrice = computed(() => {
  if (props.price.percentOff) {
    return `
      Current Price
      ${props.price.lowestSalePriceTier?.values && props.price.lowestSalePriceTier?.values[0]?.formattedValue}
      ( ${props.price.percentOff} % off).
      Previous Price
      ${props.price.regularPriceTier?.values && props.price.regularPriceTier?.values[0]?.formattedValue}
      `;
  }
  return '';
});
const isTwoRowsDisplay = computed(() => !props.price.flags.isLastAct
&& (props.price.priceType?.giftPriceLabelToAllUpcs || props.price.flags.isWithOfferPromo || props.price.promoCodeHeader));
const isClearance = computed(() => CLEARANCE_IDS.has(props.price.priceTypeId));
const isNow = computed(() => props.price.tieredPrice.some((el) => el.label?.includes('Now')) && !props.price.flags.isWithOfferPromo);
const salePriceWithOffer = computed(() => props.price.tieredPrice.at(-1).values?.[0]?.formattedValue);
const isFirstRowPriceStrike = computed(() => !!props.price.promoCodeHeader && props.price.flags.isMultiTier);
const isSecondRowPriceStrike = computed(() => !isFirstRowPriceStrike.value
  && (props.price.flags.isShowRegularPrice || (props.price.priceType?.giftPriceLabelToAllUpcs && props.price.flags.isLowestPriceRed)));

const getPriceType = computed(() => {
  if (!props.price.priceType?.text) return '';

  const priceTypeText = props.price.priceType.text.toLowerCase();

  if (priceTypeText && priceTypeText in PRICE_TYPE_RELATION) {
    return PRICE_TYPE_RELATION[priceTypeText];
  }
  return props.price.priceType.text;
});

const badgeColor = computed(() => {
  const priceType = getPriceType.value.toLowerCase();

  if (priceType === 'limited-time special' || isClearance.value) {
    return 'badge-brown';
  } if (priceType === 'our lowest price of the season' || priceType === 'today only') {
    return 'badge-red';
  } if (priceType === 'black friday deal' || priceType === 'cyber monday deal') {
    return 'badge-black';
  }

  return '';
});

const wordCode = computed(() => (props.isWordCodeEnabled
  ? `code${props.isPercentageStyleUpdate ? ':' : ''}`
  : ''
));

const toggleDetailsModal = () => {
  fireDetailsAnalytics({
    id: props.price.id,
    value: String(props.price.offers.lists?.activeOffers?.length ?? 0),
  });
  showModal.value = true;
  open(modalName);
  emit('open-details-modal');
};

const closeModal = () => {
  showModal.value = false;
  emit('close-details-modal');
};
</script>

<style lang="scss" scoped>
@import "~settingSCSS";
@import "~@csgIcons/_tooltip-small.scss";
@import "~@core/foundation/scss/foundation-6.7.4/scss/prototype/_spacing.scss";
@import "~@core/csg/src/scss/typography/typography-mixins.scss";

/* TODO: atomic-ui team to fix pricing styling based on new design */
.promo-badge.badge-brown {
  background-color: #B06207;
  color: white;
}

.promo-badge.badge-red {
  background-color: $sale-color;
  color: white;
}

.promo-badge.badge-black {
  background-color: $black;
  color: white;
}

.temp-price-heavy {
  font-weight: 700;
}

.font-weight-sm {
  font-weight: $font-weight-sm;
}

.promo-badge {
  @include body(heavy);
  background-color: $hover-color;
  padding: 2px 8px;
}

.promo-badge.new-markdown {
  background-color: $primary-color;
  color: $white;
  padding: 2px 8px;
}

.alt-display-button {
  display: inline;
}

.alt-display {
  display: block;
  margin-bottom: 4px;
}

.body-regular {
  @include body(regular);
}

.inline-block {
  display: inline-block;
}

.details-link {
  display: block;
  @include margin-direction('top', map-get($spacer, 'xxs'));
  color: $white;
}

.last-act-wrapper {
  display: flex;
  align-items: center;
}

.help-button {
  cursor: pointer;
  @extend .tooltip-small;
}

.price-red {
  color: $sale-color
}

.base-price {
  @include padding-direction('right', map-get($spacer, 'xxs'));
}

.extra-price>span {
  @include padding-direction('right', map-get($spacer, 'xxs'));
}

.price-type-container.flex-container {
  display: flex;
  align-items: baseline;
}

.price-type-container.inline-block-container {
  display: inline-block;
}

.secondary-promo {
  display: block;
  width: fit-content;
}

.additional-fees {
  font-weight: $font-weight-sm;
  margin-top: rem-calc(3.5);
}
</style>
